import { aiRouter } from '@apps/ai';
import { faRouter } from '@apps/fa';
import { systemManagementRouter } from '@apps/system-management';
import { Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router';
import { AppContainer, RootContainer, ShellContainer } from './layouts';
import { AdminLogin } from './pages/AdminLogin';
import { AdminMfaVerify } from './pages/AdminMfaVerify';
import { AuthCallbackPage } from './pages/AuthCallBack';
import { AuthErrorView } from './pages/AuthErrorView';
import { ErrorBoundary } from './pages/ErrorBoundary';
import { Login } from './pages/Login';
import { SandboxLogin } from './pages/SandboxLogin';
import { TopPage } from './pages/TopPage';
import { sanitizeForPendo } from './services/pendoSanitizer';
import { userRouter } from './userRouter';

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootContainer />}>
      <Route element={<ShellContainer />}>
        <Route index element={<TopPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin-login/mfa-verify" element={<AdminMfaVerify />} />
        <Route path="/sandbox-login" element={<SandboxLogin />} />
        <Route
          path="/user/change-password/first-time"
          lazy={async () => import('./pages/FirstTimeChangePassword')}
        />
      </Route>
      <Route path="/callback" element={<AuthCallbackPage />} />
      <Route path="/error" element={<AuthErrorView />} />
      <Route element={<AppContainer />} errorElement={<ErrorBoundary />}>
        {userRouter}
        {systemManagementRouter}
        {faRouter}
        {aiRouter}
      </Route>
      <Route path="*" lazy={async () => import('@libs/templates/template-pages/NotFound')} />
    </Route>,
  ),
);

const ACCESS_RESTRICTED_ERROR_MESSAGE = '管理者によりアクセスが制限されています。';

/**
 * ppfw-clientのTokenRefreshRequiredErrorを除外する
 */
const isTokenRefreshError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as {
    constructor?: { name?: string };
  };

  return err.constructor?.name === 'TokenRefreshRequiredError';
};

/**
 * アクセス制限エラーをPendo送信対象から除外する
 */
const isAccessRestrictedError = (error: unknown): boolean => {
  return error instanceof Error && error.message === ACCESS_RESTRICTED_ERROR_MESSAGE;
};

const pendoTrackErrorEvent = (event: string, errorInfo: object) => {
  if (window.pendo) {
    const timestamp = Date.now();
    const payload = sanitizeForPendo(event, errorInfo as Record<string, unknown>, timestamp);

    window.pendo.track('frontend_error', payload);
  }
};

function Shell() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [hasScriptError, setHasScriptError] = useState(false);

  useEffect(() => {
    // catching resource loading errors and global JS errors
    const handleErrorEvent = (event: ErrorEvent) => {
      const target = event.target;

      if (target instanceof HTMLElement) {
        // resource loading error
        const src = 'src' in target ? (target.src as string) : '';
        const href = 'href' in target ? (target.href as string) : '';
        const errorInfo = {
          tagName: target.tagName,
          id: target.id,
          className: target.className,
          src: src || href,
        };

        pendoTrackErrorEvent('resource_error', errorInfo);
      } else {
        // js error
        // ResizeObserverの警告は無害のため無視する
        if (event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
          return;
        }

        const errorInfo = {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack,
        };

        pendoTrackErrorEvent('js_error', errorInfo);
      }
    };
    window.addEventListener('error', handleErrorEvent, true);

    // catching unhandled promise exceptions
    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      // Token refresh の 401 エラーは正常フローなので除外
      if (isTokenRefreshError(event.reason)) {
        return;
      }
      // アクセス制限エラーはPendoへの送信対象外
      if (isAccessRestrictedError(event.reason)) {
        return;
      }

      let errorInfo: { type: string; message?: string; stack?: string; reason?: unknown };

      // check if reason is an error object
      if (event.reason instanceof Error) {
        errorInfo = {
          type: 'error_object',
          message: event.reason.message,
          stack: event.reason.stack,
        };
      } else {
        // handle non-Error rejections
        errorInfo = {
          type: 'non_error_object',
          reason: event.reason,
        };
      }

      pendoTrackErrorEvent('unhandled_promise_rejection', errorInfo);
    };
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    // errorBoundary routeError
    const handleReactError = (event: Event) => {
      const customEvent = event as CustomEvent;

      // Token refresh の 401 エラーは正常フローなので除外
      if (isTokenRefreshError(customEvent.detail)) {
        return;
      }
      // アクセス制限エラーはPendoへの送信対象外
      if (isAccessRestrictedError(customEvent.detail)) {
        return;
      }

      pendoTrackErrorEvent('react_error', customEvent.detail);
    };
    window.addEventListener('react-error', handleReactError);

    if (!process.env.PENDO_API_KEY) {
      setIsScriptLoaded(true);
    } else {
      const src = `https://cdn.pendo.io/agent/static/${process.env.PENDO_API_KEY}/pendo.js`;
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        setIsScriptLoaded(true);
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => setIsScriptLoaded(true);
      script.onerror = () => setHasScriptError(true);
      document.head.appendChild(script);
    }

    return () => {
      window.removeEventListener('error', handleErrorEvent, true);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
      window.removeEventListener('react-error', handleReactError);
    };
  }, []);

  if (hasScriptError) {
    return (
      <div>Failed to load external script, please refresh the page or contact system administrator.</div>
    );
  }
  if (!isScriptLoaded) {
    return null;
  }

  return (
    <Suspense>
      <RouterProvider router={router} />
    </Suspense>
  );
}

export { Shell };
